import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

import { Sheet, SheetClose, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button/button.component';
import { ModalCloseButton } from '@/components/ui/modal-close-button/modal-close-button.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import useIsMobile from '@/hooks/use-is-mobile.hook';

export type BannerSheetComponentProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  onConfirm?: () => void;
  modalTitle: string | null;
  modalDescription: string | null;
  modalButtonText: string | null;
};

export const BannerSheetComponent = ({
  open,
  setOpen,
  onConfirm,
  modalTitle,
  modalDescription,
  modalButtonText,
}: BannerSheetComponentProps) => {
  const isMobile = useIsMobile();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side={isMobile ? 'bottom' : 'centerFit'}
        closingTagPosition="right"
        hasHeader
        className="p-0 border-none overflow-x-hidden outline-none outline-offset-0"
      >
        <SheetHeader className="border-b">
          <div className="flex justify-between gap-2 px-6 pt-5 pb-4 items-center">
            <SheetTitle>
              {modalTitle && <Typography variant={TypographyVariant.MODAL_TITLE}>{modalTitle}</Typography>}
            </SheetTitle>

            <SheetClose asChild className="self-start">
              <ModalCloseButton skipInitialFocus />
            </SheetClose>
          </div>
        </SheetHeader>

        <div className="max-h-[50vh] sm:max-h-[40vh] overflow-y-auto">
          <div className="pt-2 pb-6 px-6 flex flex-col gap-5">
            {modalDescription && (
              <div className="flex flex-col gap-2">
                <div className="text-gray-700 font-normal text-wrap leading-8">
                  <ReactMarkdown
                    rehypePlugins={[rehypeRaw]}
                    components={{
                      a: ({ ...props }) => <a className="text-green-500" {...props} />,
                      ul: ({ ...props }) => <ul className="list-disc list-inside" {...props} />,
                      li: ({ ...props }) => <li className="" {...props} />,
                    }}
                  >
                    {modalDescription}
                  </ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        </div>

        <SheetFooter>
          {modalButtonText && (
            <div className="flex flex-col gap-4 w-full pt-5 px-5 pb-6 border-t">
              <div data-testid="banner-sheet-button">
                <Button variant="default" color="primary" className="w-full" onClick={onConfirm}>
                  {modalButtonText}
                </Button>
              </div>
            </div>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
